import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtDate, initials } from "@/lib/api";
import { useUsers } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";

const EMPTY = { nome: "", cognome: "", email: "", username: "", password: "" };

const UserDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(item ? { ...EMPTY, nome: item.nome, cognome: item.cognome, email: item.email, username: item.username || "" } : EMPTY); }, [open, item]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!item && f.password.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    setSaving(true);
    try {
      const body = item ? { nome: f.nome, cognome: f.cognome, email: f.email, username: f.username, ...(f.password ? { password: f.password } : {}) } : f;
      item ? await api.put(`/users/${item.id}`, body) : await api.post("/users", body);
      toast.success(item ? "Utente aggiornato" : "Utente creato");
      onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const F = (k, label, type = "text", ph = "") => (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      <Input type={type} value={f[k]} onChange={set(k)} required={k !== "password" || !item} placeholder={ph} className="rounded-xl" data-testid={`user-form-${k}`} autoComplete="off" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{item ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">{F("nome", "Nome *")}{F("cognome", "Cognome *")}</div>
          {F("email", "Email *", "email")}
          {F("username", "Username (opzionale)", "text", "es. nome.cognome")}
          {F("password", item ? "Nuova password" : "Password *", "password", item ? "Lascia vuoto per non modificarla" : "Minimo 8 caratteri")}
          <DialogFooter>
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="user-form-cancel">Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6" data-testid="user-form-submit">{saving ? "Salvataggio..." : item ? "Salva" : "Crea utente"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const { data: users, reload } = useUsers();
  const [form, setForm] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const toggleActive = async (u, v) => {
    try { await api.put(`/users/${u.id}`, { is_active: v }); toast.success(v ? "Utente riattivato" : "Utente disattivato"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/users/${del.id}`); toast.success("Utente eliminato"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Amministrazione" title="Soci e utenti" subtitle="Gestisci gli account che possono accedere a Core."
        actions={<Button onClick={() => setForm({ open: true, item: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] h-11 px-6 font-bold" data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" />Nuovo utente</Button>} />
      <Panel className="overflow-hidden core-rise core-delay-1">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {["Utente", "Email", "Username", "Ruolo", "Creato il", "Attivo", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wider">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users || []).map((u) => {
              const self = u.id === me.id;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={u.is_active ? "" : "opacity-60"}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full text-white text-sm font-bold flex items-center justify-center ${u.role === "admin" ? "bg-[#E8173A]" : "bg-[#0A1128]"}`}>{initials(u.nome, u.cognome)}</div>
                      <div className="font-bold text-slate-900">{u.nome} {u.cognome}{self && <span className="ml-2 text-xs font-semibold text-slate-400">(tu)</span>}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm" data-testid={`user-username-${u.id}`}>{u.username || "—"}</TableCell>
                  <TableCell><span className={`text-xs font-bold rounded-full px-2.5 py-1 ${u.role === "admin" ? "bg-red-50 text-[#C8102E]" : "bg-[#A6DAFC] text-[#0A1128]"}`}>{u.role === "admin" ? "Amministratore" : "Socio"}</span></TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(u.created_at?.slice(0, 10))}</TableCell>
                  <TableCell><Switch checked={u.is_active} disabled={self} onCheckedChange={(v) => toggleActive(u, v)} data-testid={`user-active-toggle-${u.id}`} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setForm({ open: true, item: u })} className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></button>
                      {!self && <button onClick={() => setDel(u)} className="p-2 rounded-lg hover:bg-red-50 text-[#E8173A]" data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Panel>
      <UserDialog open={form.open} onOpenChange={(o) => setForm((p) => ({ ...p, open: o }))} item={form.item} onSaved={reload} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare l'utente?"
        description={`L'account di ${del?.nome} ${del?.cognome} verrà eliminato. Le task assegnate resteranno senza socio.`} onConfirm={remove} />
    </div>
  );
}
